import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnChange, useUrlState } from '../../hooks/useUrlState';
import api from '../../utils/api';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faPlus, faEdit, faSearch, faCalendarAlt, faUserTie } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import EmployeeAvatar from '../../components/EmployeeAvatar'; // 👇 NEW: Modular Pagination
import { useRecentSearches } from '../../components/RecentSearches';
import { useRecentPicks } from '../../utils/recentPicks';
import { timeAgo } from '../../components/project/projectShared';
import { mountRichTextEditor } from '../../components/RichTextEditor';
import { toPreviewText } from '../../utils/richText';
import '../../styles/App.css';

const Projects = () => {
    const navigate = useNavigate();

    // --- DATA & PAGINATION STATES ---
    const [projects, setProjects] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useUrlState('page', 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // --- SEARCH STATES ---
    // The address holds the search once typing pauses; the box starts from it.
    const [debouncedSearch, setDebouncedSearch] = useUrlState('q', '');
    const [searchTerm, setSearchTerm] = useState(debouncedSearch);
    const recentSearch = useRecentSearches('project-management', { value: searchTerm, onPick: setSearchTerm });

    // Fetch Dropdown List Once
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const userRes = await api.get('/employees/project-leads');
                const managersOnly = userRes.data.filter(user => user.role === 'MANAGER');
                setUsersList(managersOnly);
            } catch (err) {
                console.error("Error fetching leads");
            }
        };
        fetchDropdowns();
    }, []);

    // Debounce Search Bar
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm, setDebouncedSearch]);

    // Reset to Page 1 on search change
    useOnChange(() => setCurrentPage(1), [debouncedSearch]);

    // Fetch Paginated Data
    useEffect(() => {
        fetchProjects(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, debouncedSearch]);

    const fetchProjects = async (pageToFetch) => {
        setLoading(true);
        try {
            const params = {
                page: pageToFetch,
                limit: itemsPerPage,
                search: debouncedSearch,
                // Active first, most recent activity first (server/routes/projects.js)
                sort: 'activity'
            };

            const projRes = await api.get('/projects/all', { params });
            
            setProjects(projRes.data.data);
            setTotalPages(projRes.data.pagination.totalPages);
            setTotalRecords(projRes.data.pagination.totalRecords);
            setCurrentPage(projRes.data.pagination.currentPage);
        } catch (err) {
            Swal.fire('Error', 'Failed to load projects', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Helper to generate the Lead options dropdown
    // `current` is the project's existing lead. The list only holds active
    // managers, so a lead who has since been deactivated is added back as
    // the selected option; otherwise the select would quietly fall back to
    // "no lead" and saving an unrelated edit would remove them.
    // The project leads picked lately are listed first.
    const recentLeads = useRecentPicks('project-lead');
    const generateUserOptions = (selectedId, current = null) => {
        const options = recentLeads.order(usersList).map(u =>
            `<option value="${u._id}" ${selectedId === u._id ? 'selected' : ''}>${u.name}</option>`
        );
        if (selectedId && current && !usersList.some(u => u._id === selectedId)) {
            options.unshift(`<option value="${selectedId}" selected>${current.name || 'Current lead'} (inactive)</option>`);
        }
        return options.join('');
    };

    const handleAddProject = async () => {
        let descBox;
        const { value: formValues } = await Swal.fire({
            title: 'Create New Project',
            width: '700px',
            html: `
                <div style="text-align: left; padding: 0 10px;">
                    <div style="display:flex; gap:15px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Project Name *</label>
                            <input id="proj-name" class="swal2-input" placeholder="e.g. Q4 Expansion" required>
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Project Lead</label>
                            <select id="proj-lead" class="swal2-select">
                                <option value="">-- Select Manager --</option>
                                ${generateUserOptions()}
                            </select>
                        </div>
                    </div>
                   
                    <div style="display:flex; gap:15px; margin-top: 10px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Start Date</label>
                            <input id="proj-start" type="date" class="swal2-input">
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Target End Date</label>
                            <input id="proj-end" type="date" class="swal2-input">
                        </div>
                    </div>

                    <div style="display:flex; gap:15px; margin-top: 10px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Total Budget (₹)</label>
                            <input id="proj-budget" type="number" class="swal2-input" placeholder="0">
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Status</label>
                            <select id="proj-status" class="swal2-select">
                                <option value="Active">Active</option>
                                <option value="On Hold">On Hold</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>
                    </div>

                    <label class="swal-custom-label" style="margin-top: 15px;">Project Description</label>
                    <div id="proj-desc"></div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Create Project',
            confirmButtonColor: '#215D7B',
            didOpen: () => { descBox = mountRichTextEditor(document.getElementById('proj-desc'), { placeholder: 'Brief details...', rows: 3 }); },
            willClose: () => descBox?.unmount(),
            preConfirm: () => {
                const name = document.getElementById('proj-name').value;
                if (!name) { Swal.showValidationMessage('Project Name is required'); return false; }
                recentLeads.record(document.getElementById('proj-lead').value);
                return {
                    name,
                    projectLead: document.getElementById('proj-lead').value,
                    startDate: document.getElementById('proj-start').value,
                    endDate: document.getElementById('proj-end').value,
                    totalBudget: document.getElementById('proj-budget').value,
                    status: document.getElementById('proj-status').value,
                    description: descBox ? descBox.getValue() : '',
                }
            }
        });

        if (formValues) {
            try {
                await api.post('/projects', formValues);
                Swal.fire('Success', 'Project created!', 'success');
                fetchProjects(currentPage);
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Failed to create project', 'error');
            }
        }
    };

    const handleEditProject = async (project) => {
        let descBox;
        const startVal = project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '';
        const endVal = project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '';
        const leadVal = project.projectLead?._id || '';

        const { value: formValues } = await Swal.fire({
            title: 'Edit Project',
            width: '700px',
            html: `
                <div style="text-align: left; padding: 0 10px;">
                    <div style="display:flex; gap:15px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Project Name *</label>
                            <input id="edit-name" class="swal2-input" value="${project.name}" required>
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Project Lead</label>
                            <select id="edit-lead" class="swal2-select">
                                <option value="">-- Select Manager --</option>
                                ${generateUserOptions(leadVal, project.projectLead)}
                            </select>
                        </div>
                    </div>
                   
                    <div style="display:flex; gap:15px; margin-top: 10px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Start Date</label>
                            <input id="edit-start" type="date" class="swal2-input" value="${startVal}">
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Target End Date</label>
                            <input id="edit-end" type="date" class="swal2-input" value="${endVal}">
                        </div>
                    </div>

                    <div style="display:flex; gap:15px; margin-top: 10px;">
                        <div style="flex:1;">
                            <label class="swal-custom-label">Total Budget (₹)</label>
                            <input id="edit-budget" type="number" class="swal2-input" value="${project.totalBudget || 0}">
                        </div>
                        <div style="flex:1;">
                            <label class="swal-custom-label">Status</label>
                            <select id="edit-status" class="swal2-select">
                                <option value="Active" ${project.status === 'Active' ? 'selected' : ''}>Active</option>
                                <option value="On Hold" ${project.status === 'On Hold' ? 'selected' : ''}>On Hold</option>
                                <option value="Completed" ${project.status === 'Completed' ? 'selected' : ''}>Completed</option>
                            </select>
                        </div>
                    </div>

                    <label class="swal-custom-label" style="margin-top: 15px;">Project Description</label>
                    <div id="edit-desc"></div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Save Changes',
            confirmButtonColor: '#215D7B',
            didOpen: () => { descBox = mountRichTextEditor(document.getElementById('edit-desc'), { value: project.description || '', rows: 3 }); },
            willClose: () => descBox?.unmount(),
            preConfirm: () => {
                const name = document.getElementById('edit-name').value;
                if (!name) { Swal.showValidationMessage('Project Name is required'); return false; }
                recentLeads.record(document.getElementById('edit-lead').value);
                return {
                    name,
                    projectLead: document.getElementById('edit-lead').value,
                    startDate: document.getElementById('edit-start').value,
                    endDate: document.getElementById('edit-end').value,
                    totalBudget: document.getElementById('edit-budget').value,
                    status: document.getElementById('edit-status').value,
                    description: descBox ? descBox.getValue() : (project.description || ''),
                }
            }
        });

        if (formValues) {
            try {
                await api.put(`/projects/${project._id}`, formValues);
                Swal.fire('Success', 'Project updated!', 'success');
                fetchProjects(currentPage); // Refresh data
            } catch (err) {
                Swal.fire('Error', 'Failed to update project', 'error');
            }
        }
    };

    return (
        <div className="settings-container fade-in">
            <div className="page-header-row mb-20">
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faFolderOpen} className="btn-icon" /> Project Registry
                </h1>
                <button className="action-btn-primary btn-small" onClick={handleAddProject}>
                    <FontAwesomeIcon icon={faPlus} className="btn-icon" /> Add Project
                </button>
            </div>

            <div className="filter-bar-card fade-in">
                <div className="search-wrapper" style={{ maxWidth: '400px' }}>
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search projects or leads..."
                        className="swal2-input search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>
            </div>

            <div className="employee-table-container fade-in">
                <ResponsiveTable className="employee-table project-registry-records">
                    <thead>
                        <tr>
                            <th>Project Details</th>
                            <th>Lead & Dates</th>
                            <th>Budget vs Spent</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="empty-table-message">Loading projects...</td></tr>
                        ) : projects.length === 0 ? (
                            <tr><td colSpan="5" className="empty-table-message">No projects found.</td></tr>
                        ) : (
                            projects.map(proj => {
                                const budget = proj.totalBudget || 0;
                                const spent = proj.totalSpent || 0;
                                const burnRate = budget > 0 ? Math.min((spent / budget) * 100, 100) : 0;
                                const isOverBudget = spent > budget && budget > 0;

                                return (
                                <tr key={proj._id}>
                                    <td data-label="Project Details">
                                        <div className="fw-bold text-primary" style={{ fontSize: '15px' }}>{proj.name}</div>
                                        <div className="text-small text-muted" style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {toPreviewText(proj.description) || 'No description provided.'}
                                        </div>
                                        <div className="text-small text-muted">
                                            {proj.lastActivityAt ? `Active ${timeAgo(proj.lastActivityAt)}` : 'No activity yet'}
                                        </div>
                                    </td>
                                   
                                    <td data-label="Lead & Dates">
                                        <div className="proj-lead">
                                            {proj.projectLead ? (
                                                <>
                                                    <EmployeeAvatar
                                                        person={proj.projectLead}
                                                        className="proj-lead-avatar"
                                                    />
                                                    <span>{proj.projectLead.name}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faUserTie} style={{ color: 'light-dark(#94a3b8, var(--theme-subtle))' }} />
                                                    <span className="text-muted">Unassigned</span>
                                                </>
                                            )}
                                        </div>
                                        <div className="text-small text-muted" style={{ marginTop: '4px' }}>
                                            <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px' }} />
                                            {proj.startDate ? new Date(proj.startDate).toLocaleDateString('en-GB') : 'TBD'}
                                            &nbsp;→&nbsp;
                                            {proj.endDate ? new Date(proj.endDate).toLocaleDateString('en-GB') : 'TBD'}
                                        </div>
                                    </td>
                                   
                                    <td data-label="Budget vs Spent">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                            <span style={{ color: 'light-dark(#64748b, var(--theme-muted))' }}>Spent: <strong>₹ {spent.toLocaleString('en-IN')}</strong></span>
                                            <span style={{ color: 'light-dark(#215D7B, var(--theme-accent-ink))' }}>Budget: ₹ {budget.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div style={{ width: '100%', height: '8px', background: 'light-dark(#e2e8f0, var(--theme-surface-muted))', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ width: `${burnRate}%`, height: '100%', background: isOverBudget ? '#dc2626' : (burnRate > 80 ? '#f59e0b' : '#16a34a') }}></div>
                                        </div>
                                        {isOverBudget && <div style={{ fontSize: '11px', color: 'light-dark(#dc2626, var(--theme-danger-ink))', marginTop: '4px', fontWeight: 'bold' }}>OVER BUDGET!</div>}
                                    </td>

                                    <td data-label="Status">
                                        <span className={`status-badge ${proj.status === 'Active' ? 'success' : proj.status === 'Completed' ? 'primary' : 'warning'}`}>
                                            {proj.status}
                                        </span>
                                    </td>
                                   
                                    <td data-label="Action">
                                        {/* The registry is the commercial record;
                                            the workspace is the work. This is the
                                            way between them (feature draft F1.2). */}
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <button
                                                className="gts-btn primary btn-small"
                                                onClick={() => { recentSearch.save(); navigate(`/projects/${proj._id}`); }}
                                            >
                                                <FontAwesomeIcon icon={faFolderOpen} className="btn-icon" /> Open
                                            </button>
                                            <button className="gts-btn btn-small" onClick={() => handleEditProject(proj)}>
                                                <FontAwesomeIcon icon={faEdit} className="btn-icon" /> Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>

            {/* 👇 Modular Pagination Component */}
            {!loading && (
                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={(newLimit) => {
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                />
            )}
        </div>
    );
};

export default Projects;
